import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/UI";
import { DotsSixVertical, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const CAT_STYLE = {
  "kaba yem": { bg: "#E4EEE6", fg: "#2D4A36" },
  "konsantre": { bg: "#FBF1DE", fg: "#8B6914" },
  "katkı": { bg: "#EAE6F3", fg: "#4A3B6B" },
  "buzağı maması": { bg: "#E6EFF3", fg: "#245667" },
  "yem": { bg: "#FBF1DE", fg: "#8B6914" },
  "diğer": { bg: "#EEECE5", fg: "#5e675e" },
};

// 'Yapım Sırası' — tüm yem/içerik kalemlerini sürükle-bırak / ok butonlarıyla sıralar.
// Sıra feed_order olarak kalıcı kaydedilir; giriş ekranı ve Excel bu sırayı kullanır.
export function FeedOrderTab({ feedOrder, reload }) {
  const [list, setList] = useState(feedOrder || []);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setList(feedOrder || []); }, [feedOrder]);

  const persist = async (newList) => {
    setSaving(true);
    try {
      await api.put("/feed-order", { names: newList.map(c => c.name) });
      toast.success("Yapım sırası kaydedildi");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Sıra kaydedilemedi");
      setList(feedOrder || []);
    } finally { setSaving(false); }
  };

  const applyMove = (from, to) => {
    if (from === null || to === null || from === to || to < 0 || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setList(next);
    persist(next);
  };

  // --- Butonlar ---
  const move = (from, to) => applyMove(from, to);

  // --- Native HTML5 sürükle-bırak (sıralama bırakma anında yapılır) ---
  const onDragStart = (e, i) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* bazı tarayıcılar */ }
  };
  const onDragOver = (e, i) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== i) setOverIndex(i);
  };
  const onDrop = (e, i) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setOverIndex(null);
    applyMove(from, i);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  return (
    <Card testId="feed-order-tab">
      <div className="mb-4">
        <h3 className="heading text-lg">Yapım Sırası</h3>
        <p className="text-xs text-[#6b746c] mt-1 max-w-2xl">
          Yemleri mikserin yapım sırasına göre dizin (sürükle-bırak veya ok tuşları). Bu sıra günlük rasyon giriş
          ekranındaki yem listesinde ve Excel çıktısında kullanılır. Sisteme yeni bir yem eklendiğinde otomatik olarak listenin en altına gelir.
        </p>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-12 text-[#8a938c]" data-testid="feed-order-empty">
          Henüz yem yok. "Yem Kataloğu" sekmesinden yem ekleyin ya da stoğa yem kalemi girin.
        </div>
      ) : (
        <ol className="space-y-1.5" data-testid="feed-order-list" onDragEnd={onDragEnd}>
          {list.map((c, i) => {
            const st = CAT_STYLE[(c.category || "").toLowerCase()] || CAT_STYLE["diğer"];
            const dragging = dragIndex === i;
            const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
            return (
              <li
                key={c.name}
                data-testid={`feed-order-row-${i}`}
                draggable
                onDragStart={(e) => onDragStart(e, i)}
                onDragOver={(e) => onDragOver(e, i)}
                onDrop={(e) => onDrop(e, i)}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-3 px-3 py-2.5 bg-white border rounded-md transition-colors select-none cursor-grab active:cursor-grabbing ${dragging ? "border-[#2D4A36] shadow-md opacity-50" : isOver ? "border-[#2D4A36] border-dashed bg-[#F3F7F1]" : "border-[#E5E2DC] hover:border-[#C9D6BE]"}`}
              >
                <DotsSixVertical size={18} weight="bold" className="text-[#b8bdb6] shrink-0 pointer-events-none" />
                <span className="tabnum text-xs text-[#8a938c] w-6 shrink-0 pointer-events-none">{i + 1}</span>
                <span className="font-semibold text-[#1A1D1A] flex-1 truncate pointer-events-none">{c.name}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 pointer-events-none" style={{ background: st.bg, color: st.fg }}>{c.category}</span>
                <div className="flex gap-0.5 shrink-0">
                  <button data-testid={`feed-order-up-${i}`} disabled={i === 0 || saving} onClick={() => move(i, i - 1)}
                    className="p-1.5 rounded hover:bg-[#f2ede4] disabled:opacity-30 disabled:cursor-not-allowed" title="Yukarı"><ArrowUp size={14} weight="bold" /></button>
                  <button data-testid={`feed-order-down-${i}`} disabled={i === list.length - 1 || saving} onClick={() => move(i, i + 1)}
                    className="p-1.5 rounded hover:bg-[#f2ede4] disabled:opacity-30 disabled:cursor-not-allowed" title="Aşağı"><ArrowDown size={14} weight="bold" /></button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
